import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  PermissionCenterDrawer,
  formatPermissionLabel,
} from '../components/PermissionCenterDrawer';
import {
  PermissionRequestProvider,
  usePermissionRequest,
} from './PermissionRequestContext';
import { createSharedContext } from './sharedContext';

const PermissionCenterContext = createSharedContext('PermissionCenter');

const normalizeRole = (role) => ({
  id: role?.id || role?.role_id || '',
  name: role?.name || role?.display_name || role?.id || role?.role_id || '',
  description: role?.description || '',
  permissions: Array.isArray(role?.permissions) ? role.permissions : [],
});

export const usePermissionCenter = () => {
  const context = useContext(PermissionCenterContext);
  if (!context) {
    throw new Error('usePermissionCenter must be used within a PermissionCenterProvider');
  }
  return context;
};

export const useOptionalPermissionCenter = () => useContext(PermissionCenterContext);

/**
 * Whether the caller holds `permission`, for showing or disabling a control
 * before it is used. A UI hint only: the backend's `@require_permission` is the
 * gate.
 *
 * Until the answer is known (no PermissionCenterProvider, still loading, or the
 * load failed) it returns `allowWhileUnknown`. The default `false` hides gated
 * UI; pass `true` where wrongly locking out someone who holds the grant is
 * worse than the 403 the hint pre-empts (e.g. a Save button).
 *
 * @param {string} permission - e.g. `'pricing.overrides:write'`
 * @param {{ allowWhileUnknown?: boolean }} [options]
 * @returns {boolean}
 */
export const usePermission = (permission, { allowWhileUnknown = false } = {}) => {
  const center = useOptionalPermissionCenter();
  if (!center || center.loading || center.error) return allowWhileUnknown;
  return center.hasPermission(permission);
};

const PermissionCenterContent = ({ children, api, appName, permissionPrefix }) => {
  const { requestAccess } = usePermissionRequest();
  const requestId = useRef(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profile, setProfile] = useState({
    permissions: [],
    roles: [],
    availablePermissions: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    const myRequest = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(
        `/my-permissions?app=${encodeURIComponent(permissionPrefix)}`
      );
      if (myRequest !== requestId.current) return;
      const data = response?.data || {};
      setProfile({
        permissions: Array.isArray(data.permissions) ? data.permissions : [],
        roles: Array.isArray(data.roles)
          ? data.roles.map(normalizeRole).filter((role) => role.id)
          : [],
        availablePermissions: Array.isArray(data.available_permissions)
          ? data.available_permissions
          : [],
      });
    } catch (err) {
      if (myRequest !== requestId.current) return;
      setProfile({ permissions: [], roles: [], availablePermissions: [] });
      setError(err);
    } finally {
      if (myRequest === requestId.current) setLoading(false);
    }
  }, [api, permissionPrefix]);

  useEffect(() => {
    refresh();
    return () => {
      requestId.current += 1;
    };
  }, [refresh]);

  const permissions = useMemo(() => new Set(profile.permissions), [profile.permissions]);
  const appPrefix = `${permissionPrefix}.`;
  const appPermissions = useMemo(
    () => profile.permissions.filter((permission) => permission.startsWith(appPrefix)),
    [appPrefix, profile.permissions]
  );
  const roles = useMemo(
    () =>
      profile.roles
        .map((role) => {
          const scopedPermissions = role.permissions.filter((permission) =>
            permission.startsWith(appPrefix)
          );
          return {
            ...role,
            permissions: scopedPermissions,
            granted:
              scopedPermissions.length > 0 &&
              scopedPermissions.every((permission) => permissions.has(permission)),
          };
        })
        .filter((role) => role.permissions.length > 0),
    [appPrefix, permissions, profile.roles]
  );
  const capabilities = useMemo(() => {
    const ids = new Set([
      ...appPermissions,
      ...profile.availablePermissions.filter((permission) =>
        permission.startsWith(appPrefix)
      ),
      ...roles.flatMap((role) => role.permissions),
    ]);
    return [...ids].sort().map((id) => ({
      id,
      label: formatPermissionLabel(id),
      granted: permissions.has(id),
    }));
  }, [appPermissions, appPrefix, permissions, profile.availablePermissions, roles]);

  const hasPermission = useCallback(
    (permission) => permissions.has(permission),
    [permissions]
  );
  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const requestRole = useCallback(
    (roleId) => {
      setDrawerOpen(false);
      requestAccess([roleId]);
    },
    [requestAccess]
  );

  const value = useMemo(
    () => ({
      permissions: profile.permissions,
      appPermissions,
      capabilities,
      roles,
      loading,
      error,
      refresh,
      hasPermission,
      drawerOpen,
      openDrawer,
      closeDrawer,
      requestRole,
    }),
    [
      appPermissions,
      capabilities,
      closeDrawer,
      drawerOpen,
      error,
      hasPermission,
      loading,
      openDrawer,
      profile.permissions,
      refresh,
      requestRole,
      roles,
    ]
  );

  return (
    <PermissionCenterContext.Provider value={value}>
      {children}
      <PermissionCenterDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        appName={appName}
        capabilities={capabilities}
        roles={roles}
        loading={loading}
        error={error}
        onRefresh={refresh}
        onRequestRole={requestRole}
      />
    </PermissionCenterContext.Provider>
  );
};

/**
 * Loads the current user's app permissions and owns the toolbar drawer and
 * existing in-app role-request dialog. Mount inside NotificationProvider.
 */
export const PermissionCenterProvider = ({
  children,
  api,
  appName,
  permissionPrefix = appName,
}) => (
  <PermissionRequestProvider api={api} appName={appName}>
    <PermissionCenterContent
      api={api}
      appName={appName}
      permissionPrefix={permissionPrefix}
    >
      {children}
    </PermissionCenterContent>
  </PermissionRequestProvider>
);

export default PermissionCenterContext;
